import { createSlice } from "@reduxjs/toolkit";
import {
  AI_BATCH_FILTER_STATUSES,
  AI_BATCH_PAGE_SIZE,
  AiBatch,
  AiBatchFilter,
  AiBatchQuota,
  AiBatchStatus,
} from "../../interfaces/AiBatchObject";
import {
  cancelAiBatch,
  fetchAiBatchDetail,
  fetchAiBatchQuota,
  fetchAiBatches,
  fetchMoreAiBatches,
  revertAiBatch,
  submitAiBatch,
} from "../../services/thunks/AiBatchThunk";
import { resetAllState } from "./resetAllState";
import {
  AsyncStatusState,
  createAsyncStatusState,
  registerAsync,
} from "./shared/registerAsync";

/**
 * State for AI batch image edits — the curator's Tasks tab and the admin's
 * AI Batch Edits section. Both read the same list: the backend scopes it from
 * the token (own batches for a curator, all for an admin).
 */
interface AiBatchState extends AsyncStatusState {
  /** The loaded pages for `filter`, newest first. */
  batches: AiBatch[];
  /** The filter `batches` belongs to. Set when a fetch STARTS, so the chips move
   *  immediately and a late response for an older filter can be recognised. */
  filter: AiBatchFilter;
  /** True when the last page came back full — there may be another. */
  hasMore: boolean;
  /** True once the first page has landed. Lets the Tasks tab tell "no tasks"
   *  from "not loaded yet" (a count chip of 0 would claim the former). */
  hasLoaded: boolean;
  /** Batches with their items, keyed by batchId. Filled when a card expands. */
  detailById: Record<string, AiBatch>;
  quotaByBusiness: Record<string, AiBatchQuota>;
}

const initialState = (): AiBatchState => ({
  batches: [],
  filter: "IN_PROGRESS",
  hasMore: false,
  hasLoaded: false,
  detailById: {},
  quotaByBusiness: {},
  ...createAsyncStatusState(),
});

// ── Keys ──────────────────────────────────────────────────────────────────────
// Components must read the exact keys the reducers write.

export const AI_BATCH_LIST_KEY = "fetchAiBatches";
export const AI_BATCH_MORE_KEY = "fetchMoreAiBatches";
export const AI_BATCH_SUBMIT_KEY = "submitAiBatch";
export const aiBatchDetailKey = (batchId: string): string => `fetchAiBatchDetail_${batchId}`;
/** Cancel and revert share one per-row key: they're never both offered, and one
 *  "this row is busy" flag is what blocks a double action. */
export const aiBatchActionKey = (batchId: string): string => `aiBatchAction_${batchId}`;
export const aiBatchQuotaKey = (businessId: string): string => `fetchAiBatchQuota_${businessId}`;

/** True when a batch in `status` belongs on a list filtered by `filter`. */
export const matchesAiBatchFilter = (
  filter: AiBatchFilter,
  status: AiBatchStatus
): boolean => filter === "ALL" || AI_BATCH_FILTER_STATUSES[filter].includes(status);

/**
 * Swap the server's version of a batch in wherever the client holds it: in
 * place in the list (order kept — a cancelled card doesn't jump or vanish under
 * the finger that cancelled it) and in the detail cache when the response
 * carries items.
 */
function applyBatch(state: AiBatchState, batch: AiBatch): void {
  const index = state.batches.findIndex((row) => row.batchId === batch.batchId);
  if (index >= 0) state.batches[index] = batch;
  if (batch.items) state.detailById[batch.batchId] = batch;
}

const aiBatchSlice = createSlice({
  name: "aiBatch",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchAiBatches — written by hand rather than through registerAsync because
    // `pending` has to record the filter. Responses for a filter the user has
    // since moved off are dropped: two quick chip taps must end on the second.
    builder
      .addCase(fetchAiBatches.pending, (state, action) => {
        state.filter = action.meta.arg;
        state.loadingMap[AI_BATCH_LIST_KEY] = "Loading AI tasks...";
        state.errorMap[AI_BATCH_LIST_KEY] = null;
        state.errorMap[AI_BATCH_MORE_KEY] = null;
      })
      .addCase(fetchAiBatches.fulfilled, (state, action) => {
        if (action.meta.arg !== state.filter) return;
        state.loadingMap[AI_BATCH_LIST_KEY] = null;
        state.batches = action.payload;
        state.hasMore = action.payload.length >= AI_BATCH_PAGE_SIZE;
        state.hasLoaded = true;
      })
      .addCase(fetchAiBatches.rejected, (state, action) => {
        if (action.meta.arg !== state.filter) return;
        state.loadingMap[AI_BATCH_LIST_KEY] = null;
        state.errorMap[AI_BATCH_LIST_KEY] =
          action.payload ?? "Couldn't load AI tasks.";
      });

    // fetchMoreAiBatches — appended, de-duplicated by id: a refresh that lands
    // while this is in flight can already hold some of the same rows.
    registerAsync(builder, fetchMoreAiBatches, {
      tab: (state) => state,
      key: AI_BATCH_MORE_KEY,
      loading: "Loading more...",
      onFulfilled: (state, page, query) => {
        if (query.filter !== state.filter) return;
        const known = new Set(state.batches.map((row) => row.batchId));
        state.batches.push(...page.filter((row) => !known.has(row.batchId)));
        state.hasMore = page.length >= AI_BATCH_PAGE_SIZE;
      },
    });

    // fetchAiBatchDetail — also refreshes the summary row, whose counts may have
    // moved on since the list was fetched.
    registerAsync(builder, fetchAiBatchDetail, {
      tab: (state) => state,
      key: aiBatchDetailKey,
      loading: "Loading items...",
      onFulfilled: applyBatch,
    });

    // submitAiBatch — shown at the top of the list if it belongs to the current
    // filter; otherwise it appears when the user switches to one it matches.
    registerAsync(builder, submitAiBatch, {
      tab: (state) => state,
      key: AI_BATCH_SUBMIT_KEY,
      loading: "Submitting batch...",
      onFulfilled: (state, batch) => {
        if (!state.hasLoaded || !matchesAiBatchFilter(state.filter, batch.status)) return;
        state.batches.unshift(batch);
      },
    });

    registerAsync(builder, cancelAiBatch, {
      tab: (state) => state,
      key: aiBatchActionKey,
      loading: "Cancelling...",
      onFulfilled: applyBatch,
    });

    registerAsync(builder, revertAiBatch, {
      tab: (state) => state,
      key: aiBatchActionKey,
      loading: "Reverting...",
      onFulfilled: applyBatch,
    });

    registerAsync(builder, fetchAiBatchQuota, {
      tab: (state) => state,
      key: aiBatchQuotaKey,
      loading: "Checking quota...",
      onFulfilled: (state, quota, businessId) => {
        // Keyed by the REQUESTED id, not the echoed one: a response that omits
        // businessId must still land where the modal will look for it.
        state.quotaByBusiness[businessId] = quota;
      },
    });

    builder.addCase(resetAllState, () => initialState());
  },
});

export default aiBatchSlice.reducer;
